import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { searchFavoriteCatalog } from "../../redux/profileSlice";
import { FAVORITE_CATALOG_TYPES } from "../../utils/profileHelpers";

const FavoriteEntityPicker = ({
  fieldKey,
  label,
  value,
  onChange,
  disabled = false,
  leagueFilterId = null,
  teamFilterId = null,
}) => {
  const catalogType = FAVORITE_CATALOG_TYPES[fieldKey];
  const [query, setQuery] = useState(value?.name || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setQuery(value?.name || "");
  }, [value?.entityId, value?.name]);

  const trimmed = useMemo(() => String(query || "").trim(), [query]);

  useEffect(() => {
    if (disabled) return undefined;
    if (fieldKey === "favoritePlayer" && trimmed.length < 2 && !teamFilterId && !leagueFilterId) {
      setItems([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const params = { q: trimmed, limit: fieldKey === "favoritePlayer" ? 30 : 20 };
        if (fieldKey === "favoriteTeam" && leagueFilterId) {
          params.leagueId = leagueFilterId;
        }
        if (fieldKey === "favoritePlayer") {
          const hasSearch = trimmed.length >= 2;
          if (!hasSearch) {
            if (teamFilterId) params.teamId = teamFilterId;
            else if (leagueFilterId) params.leagueId = leagueFilterId;
          }
        }

        const result = await searchFavoriteCatalog(catalogType, params);
        if (!cancelled) setItems(Array.isArray(result?.items) ? result.items : []);
      } catch (err) {
        if (!cancelled) {
          setItems([]);
          setError(err.message || "Search failed");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, trimmed.length >= 2 ? 280 : 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    trimmed,
    catalogType,
    disabled,
    fieldKey,
    leagueFilterId,
    teamFilterId,
  ]);

  return (
    <div className="prof-fav-picker">
      <div className="prof-fav-picker-search">
        <Search size={12} aria-hidden />
        <input
          type="search"
          className="prof-field-input prof-fav-picker-input"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          disabled={disabled}
          placeholder={
            fieldKey === "favoritePlayer"
              ? "Search player (e.g. Palmer, Mount)…"
              : `Search ${label.toLowerCase()}…`
          }
          autoFocus
        />
        {loading ? <Loader2 size={12} className="animate-spin prof-fav-picker-spin" /> : null}
      </div>

      {value?.entityId ? (
        <div className="prof-fav-selected">
          {value.logo ? (
            <img src={value.logo} alt="" className="prof-fav-selected-logo" />
          ) : (
            <span className="prof-fav-selected-fallback" aria-hidden />
          )}
          <span className="prof-fav-selected-copy">
            <strong>{value.name}</strong>
            {value.meta ? <em>{value.meta}</em> : null}
          </span>
        </div>
      ) : null}

      {error ? <p className="prof-fav-picker-error">{error}</p> : null}

      <ul className="prof-fav-picker-list" role="listbox" aria-label={`${label} results`}>
        {items.length === 0 && !loading && trimmed.length >= 2 ? (
          <li className="prof-fav-picker-empty">No matches found</li>
        ) : null}
        {items.map((item) => {
            const selected = value?.entityId === item.entityId;
            return (
              <li key={item.entityId}>
                <button
                  type="button"
                  className={`prof-fav-picker-option${selected ? " is-selected" : ""}`}
                  onClick={() => onChange(item)}
                  disabled={disabled}
                >
                  {item.logo ? (
                    <img src={item.logo} alt="" className="prof-fav-picker-logo" />
                  ) : (
                    <span className="prof-fav-picker-logo-fallback" aria-hidden />
                  )}
                  <span className="prof-fav-picker-option-copy">
                    <strong>{item.name}</strong>
                    {item.meta ? <em>{item.meta}</em> : null}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      {!items.length && !loading && fieldKey === "favoritePlayer" && trimmed.length < 2 ? (
        <p className="prof-fav-picker-hint">
          {teamFilterId || leagueFilterId
            ? "Browse the squad below or type a name to search all players."
            : "Type at least 2 characters to search players by name."}
        </p>
      ) : null}
      {!items.length && !loading && fieldKey !== "favoritePlayer" && trimmed.length < 2 ? (
        <p className="prof-fav-picker-hint">Browse the list or type to search.</p>
      ) : null}
    </div>
  );
};

export default FavoriteEntityPicker;
